/**
 * UranIA PA — Admin LLM Providers (``/admin/llm-providers``).
 *
 * Pagina admin per configurare i provider LLM **per tenant**: sostituisce
 * la singola ``ANTHROPIC_API_KEY`` da env con un set di provider
 * configurabili a runtime. Supporta:
 *
 *   - ``anthropic`` (Claude)
 *   - ``openai`` (ChatGPT)
 *   - ``grok`` (X.AI)
 *   - ``lmstudio`` (server locale OpenAI-compatibile)
 *
 * La api_key viene cifrata server-side (AES-256-GCM); la UI mostra solo
 * un hint ``****abcd``. Per LM Studio locale l'api_key e' opzionale.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';

import {
  createLlmProvider,
  deleteLlmProvider,
  fetchProviderDefaults,
  listLlmProviders,
  testLlmProvider,
  updateLlmProvider,
  LlmProvidersApiError,
} from '../../../lib/admin/llm-providers-api.ts';
import type {
  LlmProviderAdminDto,
  LlmProviderType,
  ListLlmProvidersDto,
  ProviderDefaultsMap,
  TestConnectionResult,
  UpsertLlmProviderInput,
} from '../../../lib/admin/llm-providers-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsAnyAdmin } from '../../../lib/auth/rbac.ts';
import { Route as AmministrazioneSegmentRoute } from '../../_amministrazione.tsx';

type EditingState =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'editing'; provider: LlmProviderAdminDto };

const PROVIDER_TYPES: readonly LlmProviderType[] = [
  'anthropic',
  'openai',
  'grok',
  'lmstudio',
];

function providerLabel(t: ReturnType<typeof useTranslation>['t'], pt: LlmProviderType): string {
  return t(`admin.llm_providers.types.${pt}`, { defaultValue: pt });
}

function ProviderBadge(props: { readonly type: LlmProviderType }): JSX.Element {
  const { t } = useTranslation();
  const colors: Record<LlmProviderType, { bg: string; fg: string }> = {
    anthropic: { bg: '#fef3e2', fg: '#a04400' },
    openai: { bg: '#d1f1e3', fg: '#0f5132' },
    grok: { bg: '#e1e7ff', fg: '#1a237e' },
    lmstudio: { bg: '#f0f0f0', fg: '#333' },
  };
  const c = colors[props.type];
  return (
    <span
      className="badge"
      style={{ backgroundColor: c.bg, color: c.fg, fontSize: '0.65rem' }}
    >
      {providerLabel(t, props.type)}
    </span>
  );
}

interface FormState {
  provider_type: LlmProviderType;
  label: string;
  model: string;
  base_url: string;
  api_key: string;
  scope: 'core' | 'tenant';
  is_active: boolean;
  is_primary: boolean;
  priority: number;
  timeout_seconds: number;
  max_tokens: number;
  temperature: number;
}

function defaultsToForm(
  defaults: ProviderDefaultsMap | undefined,
  pt: LlmProviderType,
): Pick<FormState, 'base_url' | 'model'> {
  if (defaults === undefined) {
    return { base_url: '', model: '' };
  }
  return { base_url: defaults[pt].base_url, model: defaults[pt].model };
}

function buildEmptyForm(defaults: ProviderDefaultsMap | undefined): FormState {
  const seed = defaultsToForm(defaults, 'anthropic');
  return {
    provider_type: 'anthropic',
    label: 'Anthropic Claude',
    model: seed.model,
    base_url: seed.base_url,
    api_key: '',
    scope: 'tenant',
    is_active: true,
    is_primary: false,
    priority: 100,
    timeout_seconds: 90,
    max_tokens: 2048,
    temperature: 0.2,
  };
}

function fromProvider(p: LlmProviderAdminDto): FormState {
  return {
    provider_type: p.provider_type,
    label: p.label,
    model: p.model,
    base_url: p.base_url,
    api_key: '',
    scope: p.tenant_id === null ? 'core' : 'tenant',
    is_active: p.is_active,
    is_primary: p.is_primary,
    priority: p.priority,
    timeout_seconds: p.timeout_seconds,
    max_tokens: p.max_tokens,
    temperature: p.temperature,
  };
}

function ProviderEditor(props: {
  readonly mode: 'create' | 'edit';
  readonly providerId?: string;
  readonly initial: FormState;
  readonly defaults: ProviderDefaultsMap | undefined;
  readonly onSaved: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(props.initial);
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const isEdit = props.mode === 'edit';

  useEffect(() => {
    setForm(props.initial);
  }, [props.initial]);

  const upsert = useMutation<LlmProviderAdminDto, LlmProvidersApiError, UpsertLlmProviderInput>({
    mutationFn: async (input) => {
      if (isEdit && props.providerId !== undefined) {
        return await updateLlmProvider(props.providerId, input);
      }
      return await createLlmProvider(input);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'llm-providers'] });
      setSavedHint(t('admin.llm_providers.form.saved'));
      props.onSaved();
    },
  });

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleProviderTypeChange = (pt: LlmProviderType): void => {
    const seed = defaultsToForm(props.defaults, pt);
    setForm((prev) => ({
      ...prev,
      provider_type: pt,
      base_url: prev.base_url === '' ? seed.base_url : prev.base_url,
      model: prev.model === '' ? seed.model : prev.model,
    }));
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setSavedHint(null);
    const payload: UpsertLlmProviderInput = {
      provider_type: form.provider_type,
      label: form.label.trim(),
      model: form.model.trim(),
      base_url: form.base_url.trim(),
      // In create: invia sempre l'api_key (anche vuota per LM Studio).
      // In edit: invia api_key solo se l'utente l'ha inserita (rimpiazzo); altrimenti null = mantieni.
      api_key: isEdit
        ? form.api_key.length > 0
          ? form.api_key
          : null
        : form.api_key,
      scope: form.scope,
      is_active: form.is_active,
      is_primary: form.is_primary,
      priority: form.priority,
      timeout_seconds: form.timeout_seconds,
      max_tokens: form.max_tokens,
      temperature: form.temperature,
    };
    upsert.mutate(payload);
  };

  return (
    <form onSubmit={onSubmit} data-testid="admin-llm-providers-form">
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.provider_type')}
          </label>
          <select
            className="form-select form-select-sm"
            value={form.provider_type}
            onChange={(e): void => handleProviderTypeChange(e.target.value as LlmProviderType)}
            data-testid="admin-llm-providers-form-type"
          >
            {PROVIDER_TYPES.map((pt) => (
              <option key={pt} value={pt}>
                {providerLabel(t, pt)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.label')}
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={form.label}
            onChange={(e): void => handleChange('label', e.target.value)}
            required
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.scope')}
          </label>
          <select
            className="form-select form-select-sm"
            value={form.scope}
            onChange={(e): void => handleChange('scope', e.target.value as 'core' | 'tenant')}
          >
            <option value="tenant">{t('admin.llm_providers.form.scope_tenant')}</option>
            <option value="core">{t('admin.llm_providers.form.scope_core')}</option>
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.model')}
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={form.model}
            onChange={(e): void => handleChange('model', e.target.value)}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.base_url')}
          </label>
          <input
            type="url"
            className="form-control form-control-sm"
            value={form.base_url}
            onChange={(e): void => handleChange('base_url', e.target.value)}
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.api_key')}
            {isEdit && (
              <span className="text-muted ms-2 fst-italic">
                {t('admin.llm_providers.form.api_key_edit_hint')}
              </span>
            )}
          </label>
          <input
            type="password"
            className="form-control form-control-sm"
            value={form.api_key}
            onChange={(e): void => handleChange('api_key', e.target.value)}
            placeholder={
              form.provider_type === 'lmstudio'
                ? t('admin.llm_providers.form.api_key_lmstudio_placeholder')
                : t('admin.llm_providers.form.api_key_placeholder')
            }
            autoComplete="off"
          />
        </div>

        <div className="col-md-3">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.priority')}
          </label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={form.priority}
            min={0}
            max={999}
            onChange={(e): void => handleChange('priority', Number(e.target.value))}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.timeout_seconds')}
          </label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={form.timeout_seconds}
            min={5}
            max={600}
            step={5}
            onChange={(e): void => handleChange('timeout_seconds', Number(e.target.value))}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.max_tokens')}
          </label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={form.max_tokens}
            min={64}
            max={200000}
            step={64}
            onChange={(e): void => handleChange('max_tokens', Number(e.target.value))}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1">
            {t('admin.llm_providers.form.temperature')}
          </label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={form.temperature}
            min={0}
            max={2}
            step={0.1}
            onChange={(e): void => handleChange('temperature', Number(e.target.value))}
          />
        </div>

        <div className="col-md-6">
          <div className="form-check mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="llm-provider-is-active"
              checked={form.is_active}
              onChange={(e): void => handleChange('is_active', e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="llm-provider-is-active">
              {t('admin.llm_providers.form.is_active')}
            </label>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-check mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="llm-provider-is-primary"
              checked={form.is_primary}
              onChange={(e): void => handleChange('is_primary', e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="llm-provider-is-primary">
              {t('admin.llm_providers.form.is_primary')}
            </label>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
        <div>
          {upsert.isError && (
            <div className="text-danger small" role="alert">
              {upsert.error.message}
              {upsert.error.detail !== null && `: ${upsert.error.detail}`}
            </div>
          )}
          {savedHint !== null && upsert.isSuccess && (
            <div className="text-success small" role="status">
              {savedHint}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={upsert.isPending}
          data-testid="admin-llm-providers-form-submit"
        >
          {upsert.isPending
            ? t('admin.llm_providers.form.saving')
            : isEdit
              ? t('admin.llm_providers.form.update')
              : t('admin.llm_providers.form.create')}
        </button>
      </div>
    </form>
  );
}

export function AdminLlmProvidersPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isAdmin = principalIsAnyAdmin(principal);
  const qc = useQueryClient();

  const [editing, setEditing] = useState<EditingState>({ kind: 'idle' });
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | LlmProviderType>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [testResult, setTestResult] = useState<{ id: string; result: TestConnectionResult } | null>(
    null,
  );

  const query = useQuery<ListLlmProvidersDto, LlmProvidersApiError>({
    queryKey: ['admin', 'llm-providers'],
    queryFn: listLlmProviders,
    enabled: isAuthenticated && isAdmin,
    staleTime: 15_000,
  });

  const defaultsQuery = useQuery<ProviderDefaultsMap>({
    queryKey: ['admin', 'llm-providers', 'defaults'],
    queryFn: fetchProviderDefaults,
    enabled: isAuthenticated && isAdmin,
    staleTime: 60_000,
  });

  const deleteMutation = useMutation<void, LlmProvidersApiError, string>({
    mutationFn: deleteLlmProvider,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'llm-providers'] });
    },
  });

  const testMutation = useMutation<TestConnectionResult, LlmProvidersApiError, string>({
    mutationFn: testLlmProvider,
    onSuccess: (result, id) => {
      setTestResult({ id, result });
    },
  });

  const filtered = useMemo(() => {
    const items = query.data?.items ?? [];
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (typeFilter !== 'all' && p.provider_type !== typeFilter) return false;
      if (activeFilter === 'active' && !p.is_active) return false;
      if (activeFilter === 'inactive' && p.is_active) return false;
      if (q.length === 0) return true;
      return (
        p.label.toLowerCase().includes(q) ||
        p.model.toLowerCase().includes(q) ||
        p.base_url.toLowerCase().includes(q)
      );
    });
  }, [query.data?.items, search, typeFilter, activeFilter]);

  const hasFiltersActive =
    search.length > 0 || typeFilter !== 'all' || activeFilter !== 'all';

  if (!isAuthenticated) {
    return (
      <section data-testid="admin-llm-providers-unauthenticated">
        <h1 className="h3 mb-3">{t('admin.llm_providers.title')}</h1>
        <div className="alert alert-info">
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link to="/accedi" className="btn btn-primary">
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }
  if (!isAdmin) {
    return (
      <section data-testid="admin-llm-providers-forbidden">
        <h1 className="h3 mb-3">{t('admin.llm_providers.title')}</h1>
        <div className="alert alert-warning">
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  const initialForm: FormState =
    editing.kind === 'editing'
      ? fromProvider(editing.provider)
      : buildEmptyForm(defaultsQuery.data);

  return (
    <section className="urania-admin-llm-providers" data-testid="admin-llm-providers-page">
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          ← {t('admin.console.title')}
        </Link>
      </nav>

      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div style={{ maxWidth: 720 }}>
            <span
              className="badge mb-2"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
              }}
            >
              ADMIN · MULTI-LLM
            </span>
            <h1 className="h3 mb-2">{t('admin.llm_providers.title')}</h1>
            <p className="mb-0 opacity-90 small">{t('admin.llm_providers.subtitle')}</p>
          </div>
          <button
            type="button"
            className="btn btn-light"
            onClick={(): void => setEditing({ kind: 'creating' })}
            data-testid="admin-llm-providers-new"
          >
            + {t('admin.llm_providers.actions.new')}
          </button>
        </div>
      </div>

      {query.isLoading && (
        <p role="status" aria-live="polite">
          {t('admin.llm_providers.loading')}
        </p>
      )}
      {query.isError && (
        <div className="alert alert-danger" role="alert">
          <strong>{t('admin.llm_providers.errors.load_failed')}</strong>
          {query.error.detail !== null && <p className="mb-0">{query.error.detail}</p>}
        </div>
      )}

      {editing.kind !== 'idle' && (
        <div className="card shadow-sm border-0 mb-4" data-testid="admin-llm-providers-editor">
          <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {editing.kind === 'creating'
                ? t('admin.llm_providers.form.heading_create')
                : t('admin.llm_providers.form.heading_edit', {
                    label: editing.provider.label,
                  })}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={(): void => setEditing({ kind: 'idle' })}
            >
              {t('admin.llm_providers.actions.close_editor')}
            </button>
          </div>
          <div className="card-body">
            <ProviderEditor
              mode={editing.kind === 'creating' ? 'create' : 'edit'}
              {...(editing.kind === 'editing' ? { providerId: editing.provider.id } : {})}
              initial={initialForm}
              defaults={defaultsQuery.data}
              onSaved={(): void => {
                setTimeout(() => setEditing({ kind: 'idle' }), 1500);
              }}
            />
          </div>
        </div>
      )}

      {query.isSuccess && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body py-2 px-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-5">
                <label className="form-label small text-muted mb-1">
                  {t('admin.llm_providers.filters.search_label')}
                </label>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  value={search}
                  onChange={(e): void => setSearch(e.target.value)}
                  placeholder={t('admin.llm_providers.filters.search_placeholder')}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">
                  {t('admin.llm_providers.filters.type_label')}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={typeFilter}
                  onChange={(e): void => setTypeFilter(e.target.value as 'all' | LlmProviderType)}
                >
                  <option value="all">{t('admin.llm_providers.filters.type_all')}</option>
                  {PROVIDER_TYPES.map((pt) => (
                    <option key={pt} value={pt}>
                      {providerLabel(t, pt)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">
                  {t('admin.llm_providers.filters.active_label')}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={activeFilter}
                  onChange={(e): void => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
                >
                  <option value="all">{t('admin.llm_providers.filters.active_all')}</option>
                  <option value="active">{t('admin.llm_providers.status.active')}</option>
                  <option value="inactive">{t('admin.llm_providers.status.inactive')}</option>
                </select>
              </div>
              <div className="col-md-1 d-flex justify-content-end">
                {hasFiltersActive && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={(): void => {
                      setSearch('');
                      setTypeFilter('all');
                      setActiveFilter('all');
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            {hasFiltersActive && (
              <p className="small text-muted mb-0 mt-2">
                {t('admin.llm_providers.filters.results_count', {
                  shown: filtered.length,
                  total: query.data.total,
                })}
              </p>
            )}
          </div>
        </div>
      )}

      {query.isSuccess && (
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-bottom py-2 px-3">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {t('admin.llm_providers.list_heading', { count: query.data.total })}
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0" data-testid="admin-llm-providers-table">
              <thead>
                <tr>
                  <th>{t('admin.llm_providers.columns.label')}</th>
                  <th>{t('admin.llm_providers.columns.provider')}</th>
                  <th>{t('admin.llm_providers.columns.model')}</th>
                  <th>{t('admin.llm_providers.columns.api_key_hint')}</th>
                  <th>{t('admin.llm_providers.columns.priority')}</th>
                  <th>{t('admin.llm_providers.columns.status')}</th>
                  <th className="text-end">
                    <span className="visually-hidden">
                      {t('admin.llm_providers.columns.actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted small fst-italic py-3">
                      {t('admin.llm_providers.filters.empty')}
                    </td>
                  </tr>
                )}
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`admin-llm-providers-row-${p.id}`}
                    style={{ opacity: p.is_active ? 1 : 0.6 }}
                  >
                    <td>
                      <strong>{p.label}</strong>
                      {p.is_primary && (
                        <span
                          className="badge ms-2"
                          style={{
                            backgroundColor: '#fff3cd',
                            color: '#664d03',
                            fontSize: '0.6rem',
                          }}
                        >
                          {t('admin.llm_providers.badges.primary')}
                        </span>
                      )}
                      <br />
                      <small className="text-muted">{p.base_url}</small>
                    </td>
                    <td>
                      <ProviderBadge type={p.provider_type} />
                    </td>
                    <td>
                      <code className="small">{p.model}</code>
                    </td>
                    <td>
                      <code className="small">{p.api_key_hint || '—'}</code>
                    </td>
                    <td>{p.priority}</td>
                    <td>
                      {p.is_active ? (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#d1e7dd',
                            color: '#0f5132',
                            fontSize: '0.65rem',
                          }}
                        >
                          {t('admin.llm_providers.status.active')}
                        </span>
                      ) : (
                        <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                          {t('admin.llm_providers.status.inactive')}
                        </span>
                      )}
                      {testResult !== null && testResult.id === p.id && (
                        <div className="small mt-1">
                          {testResult.result.ok ? (
                            <span className="text-success">
                              ✓ {testResult.result.detail}
                              {testResult.result.models_count !== null
                                ? ` (${testResult.result.models_count} ${t('admin.llm_providers.test.models')})`
                                : ''}
                            </span>
                          ) : (
                            <span className="text-danger" title={testResult.result.detail}>
                              ✗ {t('admin.llm_providers.test.failed')}
                              {testResult.result.status_code !== null
                                ? ` (${testResult.result.status_code})`
                                : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={(): void => testMutation.mutate(p.id)}
                          disabled={testMutation.isPending}
                          data-testid={`admin-llm-providers-test-${p.id}`}
                        >
                          {t('admin.llm_providers.actions.test')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={(): void => setEditing({ kind: 'editing', provider: p })}
                          data-testid={`admin-llm-providers-edit-${p.id}`}
                        >
                          {t('admin.llm_providers.actions.edit')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={(): void => {
                            if (window.confirm(t('admin.llm_providers.actions.delete_confirm'))) {
                              deleteMutation.mutate(p.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          data-testid={`admin-llm-providers-delete-${p.id}`}
                        >
                          {t('admin.llm_providers.actions.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => AmministrazioneSegmentRoute,
  path: '/admin/llm-providers',
  component: AdminLlmProvidersPage,
});
